// DG Capacity Utilisation Report — the management PDF.
//
// WHO IT IS FOR
// =============
// Management deciding whether to replace a diesel generator with a smaller one.
// The report therefore leads with the evidence and ends with a decision per DG,
// and it states — on the first page, again beside the recommendations, and in
// every footer — that the loads are full-tank-to-full-tank AVERAGES and do not
// capture peak demand. A reader must not be able to take a recommendation away
// without that caveat.
//
// ORDER (as requested)
// ====================
//   Scope      period analysed, every filter applied (or "None"), data basis,
//              and the peak-usage limitation
//   1. Table   one row per DG, with a portfolio total
//   2. Graphs  utilisation over the period — all DGs together, then each DG
//   3. Recommendations  decision summary, a recommendation per DG, and what to
//              confirm before approving a smaller DG
//   Appendix   method, assumptions used, how each kVA rating was confirmed, the
//              fleet fuel curve, and every exclusion with its reason
//
// DATA RULES
// ==========
//   - Every figure comes from the analysis the screen shows. Nothing here
//     re-derives a business number; the one extra count (intervals whose average
//     load exceeds a recommended rating) comes from the engine.
//   - Missing values print as "-", never 0. A true zero prints as 0.
//   - Money appears only when prices were entered in the report run; otherwise
//     the report says prices were not entered.
//   - Charts are drawn as vector lines with jsPDF so they print sharply and need
//     no canvas. Lines break at excluded intervals, exactly as on screen.
//   - The standard PDF fonts only cover Latin-1, so text passes through pdfSafe
//     (em dash -> "-", "×" -> "x", "₹" -> "Rs") rather than printing garbage.

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

import { ASSETPRO_LOGO_WHITE_PNG, LOGO_ASPECT } from './registerRecon/brandAssets';
import { countIntervalsAboveRating } from './dgOperatingLevel';

// AssetPro Design Foundations, light-surface tokens (same as reconPdf).
const NAVY = [10, 18, 32];
const ORANGE = [255, 107, 25];
const GREEN = [31, 168, 98];
const RED = [226, 59, 65];
const AMBER = [217, 138, 0];
const TEXT_BODY = [34, 52, 72];
const TEXT_MUTED = [110, 134, 159];
const SURFACE_RAISED = [241, 244, 249];
const BORDER = [214, 222, 233];
const GRID = [232, 237, 244];
const AXIS = [160, 175, 192];
const AMBER_SURFACE = [255, 244, 229];

// Distinct, print-safe series colours; a DG keeps the same colour in every chart.
const PALETTE = [
  [30, 136, 250], [255, 107, 25], [31, 168, 98], [226, 59, 65], [130, 87, 229], [0, 150, 160],
  [217, 138, 0], [214, 51, 132], [90, 110, 130], [120, 170, 40], [180, 90, 40], [60, 60, 160],
];

const DAY_MS = 86400000;

export const LIMITATION_TITLE = 'This report does not consider peak usage';
export const LIMITATION_TEXT =
  'Every load figure in this report is an average over a full-tank-to-full-tank interval: the kWh generated ' +
  'between two refuelling events divided by the hours run in between. Peak demand is not measured. Short peaks, ' +
  'motor starts and step loads can be materially higher than these averages, so a recommended size is a ' +
  'screening candidate only. Confirm peak demand with short-interval load logging before replacing any DG with a ' +
  'smaller one.';

const CHECKLIST = [
  'Log 15-minute kW, kVA and power factor for 7-14 representative days, including the busiest operating period.',
  'Record the largest motor starting current and the step-load sequence the set must pick up.',
  'Apply site derating for altitude, ambient temperature and harmonic loads.',
  'Decide the redundancy requirement - single set, N+1 or parallel operation.',
  'Obtain OEM fuel curves and delivered, tax-inclusive quotations for the recommended size.',
];

export const RECOMMENDATION = {
  DOWNSIZE: 'DOWNSIZE',
  RETAIN: 'RETAIN',
  VALIDATE: 'VALIDATE',
  NOT_ASSESSED: 'NOT_ASSESSED',
};

const DECISION_LABEL = {
  DOWNSIZE: 'Smaller DG',
  RETAIN: 'Retain',
  VALIDATE: 'Validate metering',
  NOT_ASSESSED: 'Not assessed',
};

const DECISION_COLOR = {
  DOWNSIZE: GREEN,
  RETAIN: TEXT_BODY,
  VALIDATE: AMBER,
  NOT_ASSESSED: TEXT_MUTED,
};

const DECISION_RANK = { DOWNSIZE: 0, RETAIN: 1, VALIDATE: 2, NOT_ASSESSED: 3 };

// --- formatting ---------------------------------------------------------------

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Replace characters the standard PDF fonts cannot draw. */
export const pdfSafe = (text) =>
  String(text === null || text === undefined ? '' : text)
    .replace(/[–—]/g, '-')
    .replace(/→/g, '->')
    .replace(/×/g, 'x')
    .replace(/÷/g, '/')
    .replace(/₹\s?/g, 'Rs ')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, '...')
    .replace(/≥/g, '>=')
    .replace(/≤/g, '<=');

const f = (v, dp = 0) =>
  isNum(v) ? v.toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : '-';
const pc = (v, dp = 1) => (isNum(v) ? `${(v * 100).toFixed(dp)}%` : '-');
const kva = (v) => (isNum(v) ? (Number.isInteger(v) ? String(v) : v.toFixed(1)) : '-');
const rs = (v) => (isNum(v) ? `Rs ${f(v)}` : '-');
const fmtDate = (v) => moment(v).format('DD MMM YYYY');

// --- report content rules (pure, tested) -------------------------------------

/**
 * The filters applied in the report run, as label/value pairs. Empty when none —
 * the report then says so explicitly rather than leaving the reader to infer it.
 */
export const describeFilters = (filters = {}) => {
  const items = [];
  const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
  if (list(filters.locations).length) items.push(['Location', list(filters.locations).join(', ')]);
  if (list(filters.subLocations).length) items.push(['Sub location', list(filters.subLocations).join(', ')]);
  if (list(filters.models).length) items.push(['Asset model', list(filters.models).join(', ')]);
  const search = typeof filters.assetSearch === 'string' ? filters.assetSearch.trim() : '';
  if (search) items.push(['Asset / owner code contains', `"${search}"`]);
  return items;
};

/**
 * The management decision for one DG, with the reasoning written out in full.
 *
 * @param {Object} row a summariseAsset row
 * @param {{intervals: Array, assumptions: Object}} context
 * @returns {{category: string, decision: string, text: string}}
 */
export const recommendationFor = (row, { intervals = [], assumptions }) => {
  const make = (category, text) => ({ category, decision: DECISION_LABEL[category], text });
  const pf = assumptions.powerFactor;
  const target = assumptions.targetLoading;

  if (!isNum(row.ratedKva)) {
    return make(
      RECOMMENDATION.NOT_ASSESSED,
      'Rated kVA was not confirmed for this asset model, so utilisation and sizing were not calculated.'
    );
  }
  if (!row.intervalCount) {
    return make(RECOMMENDATION.NOT_ASSESSED, 'No measurable full-tank-to-full-tank interval in the period.');
  }
  if (row.meterSuspect) {
    return make(
      RECOMMENDATION.VALIDATE,
      `Overall efficiency of ${f(row.efficiencyKwhPerL, 2)} kWh/L is outside the plausible ` +
        `${assumptions.minPlausibleEfficiency}-${assumptions.maxPlausibleEfficiency} kWh/L band, which points to a ` +
        'kWh or hour meter problem. No size is recommended until the metering is verified.'
    );
  }

  const thin =
    row.intervalCount < assumptions.minIntervalsForP95
      ? ` Only ${row.intervalCount} interval(s) were measured, so the P95 load is a thin estimate.`
      : '';

  if (isNum(row.recommendedKva) && row.recommendedKva < row.ratedKva) {
    const above = countIntervalsAboveRating(intervals, row.recommendedKva, pf);
    const aboveText = above
      ? ` ${above.above} of ${above.total} interval(s) averaged more than the ${kva(row.recommendedKva)} kVA ` +
        `set's rated ${f(above.limitKw, 1)} kW.`
      : '';
    let saving;
    if (isNum(row.annualisedSavingL)) {
      saving =
        ` Estimated fuel saving about ${f(row.annualisedSavingL)} L/year` +
        (isNum(row.paybackMonths) ? `, simple payback ${f(row.paybackMonths, 1)} months.` : '.');
    } else if (isNum(row.periodSavingL)) {
      saving = ` Estimated saving ${f(row.periodSavingL)} L over the period; not annualised - ${row.annualisationReason}.`;
    } else {
      saving = ` No fuel saving is stated - ${row.savingReason}.`;
    }
    return make(
      RECOMMENDATION.DOWNSIZE,
      `Consider a ${kva(row.recommendedKva)} kVA set in place of the ${kva(row.ratedKva)} kVA set. ` +
        `Average utilisation is ${pc(row.utilisation)}. The 95th-percentile interval load of ${f(row.p95Kw, 1)} kW ` +
        `needs ${f(row.requiredKva, 1)} kVA at ${pc(target, 0)} loading; a ${kva(row.recommendedKva)} kVA set would ` +
        `carry it at ${pc(row.p95LoadingAfter, 0)}.${aboveText}${saving}${thin}`
    );
  }

  return make(
    RECOMMENDATION.RETAIN,
    `Retain the current ${kva(row.ratedKva)} kVA set. Average utilisation is ${pc(row.utilisation)} and the ` +
      `95th-percentile interval load of ${f(row.p95Kw, 1)} kW needs ${f(row.requiredKva, 1)} kVA at ` +
      `${pc(target, 0)} loading. ` +
      `${row.recommendationReason ? `${row.recommendationReason}.` : 'No smaller standard size carries that load.'}${thin}`
  );
};

/** How the rated kVA used for a model was arrived at, for the audit trail. */
export const ratingBasis = (model, confirmed) => {
  const has = confirmed !== undefined && confirmed !== null && confirmed !== '' && isNum(Number(confirmed));
  if (!has) return 'Not confirmed - these DGs are excluded from utilisation, sizing and saving';
  if (!model.suggestion) return 'Entered by user (no kVA rating in the name)';
  if (Number(confirmed) === model.suggestion.value) {
    return `As parsed from ${model.suggestion.field}: "${model.suggestion.text}"`;
  }
  return `Corrected by user (name suggested ${model.suggestion.value} kVA from "${model.suggestion.text}")`;
};

// --- chart drawing -----------------------------------------------------------

const drawChart = (doc, opts) => {
  const { x, y, w, h, xMin, xMax, lines = [], refLines = [], title, subtitle, emptyText, legend = false } = opts;

  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.25);
  doc.roundedRect(x, y, w, h, 1.5, 1.5, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...NAVY);
  doc.text(pdfSafe(title), x + 3, y + 5);
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(pdfSafe(subtitle), x + 3, y + 9);
  }

  const legendW = legend ? 44 : 0;
  const left = x + 14;
  const right = x + w - 4 - legendW;
  const top = y + (subtitle ? 13 : 9);
  const bottom = y + h - 7;
  const plotW = right - left;
  const plotH = bottom - top;

  if (emptyText) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.text(pdfSafe(emptyText), (left + right) / 2, (top + bottom) / 2, { align: 'center' });
    return;
  }

  let peak = 0;
  lines.forEach((line) =>
    line.points.forEach((p) => {
      if (!p.isBreak && isNum(p.utilisation)) peak = Math.max(peak, p.utilisation * 100);
    })
  );
  refLines.forEach((r) => {
    peak = Math.max(peak, r.value);
  });
  // A fixed 0-125% scale keeps every DG comparable at a glance; it only grows if
  // a value genuinely exceeds it.
  const yMax = Math.max(125, Math.ceil(peak / 25) * 25);
  const span = xMax - xMin || 1;
  const xPos = (ms) => left + ((ms - xMin) / span) * plotW;
  const yPos = (v) => bottom - (v / yMax) * plotH;

  doc.setLineWidth(0.15);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  for (let t = 0; t <= yMax; t += 25) {
    doc.setDrawColor(...(t === 0 ? AXIS : GRID));
    doc.line(left, yPos(t), right, yPos(t));
    doc.setTextColor(...TEXT_MUTED);
    doc.text(`${t}%`, left - 1.5, yPos(t) + 1, { align: 'right' });
  }
  const tickFormat = span > 300 * DAY_MS ? 'DD MMM YY' : 'DD MMM';
  for (let i = 0; i <= 4; i += 1) {
    const ms = xMin + (span * i) / 4;
    doc.text(moment(ms).format(tickFormat), xPos(ms), bottom + 4, { align: 'center' });
  }

  refLines.forEach((r) => {
    doc.setDrawColor(...r.color);
    doc.setLineWidth(0.3);
    doc.setLineDashPattern([1.2, 1], 0);
    doc.line(left, yPos(r.value), right, yPos(r.value));
    doc.setLineDashPattern([], 0);
    doc.setFontSize(6);
    doc.setTextColor(...r.color);
    doc.text(pdfSafe(r.label), right - 1, yPos(r.value) - 1, { align: 'right' });
  });

  lines.forEach((line) => {
    doc.setDrawColor(...line.color);
    doc.setFillColor(...line.color);
    doc.setLineWidth(0.4);
    let previous = null;
    line.points.forEach((p) => {
      // A break point ends the line: nothing was measured across that stretch.
      if (p.isBreak || !isNum(p.utilisation)) {
        previous = null;
        return;
      }
      const px = xPos(p.ms);
      const py = yPos(p.utilisation * 100);
      if (previous) doc.line(previous[0], previous[1], px, py);
      doc.circle(px, py, 0.55, 'F');
      previous = [px, py];
    });
  });

  if (legend) {
    const lx = right + 6;
    const rowH = 3.6;
    const maxRows = Math.max(1, Math.floor(plotH / rowH));
    let ly = top + 2;
    doc.setFontSize(6.5);
    lines.some((line, i) => {
      if (i === maxRows - 1 && lines.length > maxRows) {
        doc.setTextColor(...TEXT_MUTED);
        doc.text(`+${lines.length - i} more DGs`, lx, ly);
        return true;
      }
      doc.setFillColor(...line.color);
      doc.rect(lx, ly - 1.8, 3, 1.6, 'F');
      doc.setTextColor(...TEXT_BODY);
      doc.text(pdfSafe(line.label), lx + 4.5, ly);
      ly += rowH;
      return false;
    });
  }
};

// --- the document --------------------------------------------------------------

/**
 * @param {Object} args
 * @param {Object} args.analysis   the page's analysis (assetRows, allIntervals, curve,
 *                                 portfolio, dropReasons, utilisationSeries…)
 * @param {Object} args.scope      the loaded period (queriedRange, rangeStartMs/EndMs,
 *                                 assets, totalEntries, models)
 * @param {Object} args.filters    { locations, subLocations, models, assetSearch }
 * @param {Object} args.assumptions
 * @param {number|null} args.dieselPrice
 * @param {Object} args.priceMap   { [kva]: price }
 * @param {Object} args.modelKva   { [modelKey]: confirmed kVA }
 * @param {string} [args.serverLabel]
 * @param {Date}   [args.generatedAt]
 * @returns {jsPDF}
 */
export const buildDgReportPdf = ({
  analysis,
  scope,
  filters = {},
  assumptions,
  dieselPrice = null,
  priceMap = {},
  modelKva = {},
  serverLabel = null,
  generatedAt = new Date(),
}) => {
  if (!analysis || !scope) throw new Error('No analysis to export - load a period first.');
  if (!analysis.assetRows || analysis.assetRows.length === 0) {
    throw new Error('No DG is in view with the current filters, so there is nothing to report.');
  }

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 12;
  const TOP = 16;
  const BOTTOM = pageH - 14;
  const contentW = pageW - M * 2;
  let y = 0;

  const [rangeStart, rangeEnd] = scope.queriedRange;
  const periodLabel = `${fmtDate(rangeStart)} to ${fmtDate(rangeEnd)}`;
  const rows = analysis.assetRows;
  const portfolio = analysis.portfolio;

  const room = (needed) => {
    if (y + needed > BOTTOM) {
      doc.addPage();
      y = TOP;
    }
  };
  // Graphs, recommendations and the appendix each open on a fresh page, so a
  // reader never finds a section heading stranded at the foot of the page before.
  const newPage = () => {
    if (y > TOP + 1) {
      doc.addPage();
      y = TOP;
    }
  };
  const setText = (size, style = 'normal', color = TEXT_BODY) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };
  const section = (title) => {
    room(24);
    doc.setFillColor(...NAVY);
    doc.rect(M, y, contentW, 8, 'F');
    doc.setFillColor(...ORANGE);
    doc.rect(M, y, 1.6, 8, 'F');
    setText(10.5, 'bold', [255, 255, 255]);
    doc.text(pdfSafe(title), M + 5, y + 5.5);
    y += 12;
  };
  const subheading = (title, needed = 12) => {
    room(needed);
    setText(9.5, 'bold', NAVY);
    doc.text(pdfSafe(title), M, y + 3);
    y += 6.5;
  };
  const para = (text, color = TEXT_BODY, size = 8) => {
    setText(size, 'normal', color);
    const lines = doc.splitTextToSize(pdfSafe(text), contentW);
    room(lines.length * 3.7 + 2);
    doc.text(lines, M, y + 3);
    y += lines.length * 3.7 + 2.5;
  };
  const cell = (c) => (c && typeof c === 'object' ? { ...c, content: pdfSafe(c.content) } : pdfSafe(c));
  const table = (head, body, opts = {}) => {
    const { foot, ...rest } = opts;
    autoTable(doc, {
      startY: y,
      head: head ? [head.map(cell)] : undefined,
      body: body.map((r) => r.map(cell)),
      foot: foot ? foot.map((r) => r.map(cell)) : undefined,
      showFoot: 'lastPage',
      styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak', textColor: TEXT_BODY, lineColor: BORDER, lineWidth: 0.1, valign: 'middle' },
      alternateRowStyles: { fillColor: SURFACE_RAISED },
      headStyles: { fillColor: NAVY, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7 },
      footStyles: { fillColor: [226, 232, 240], textColor: NAVY, fontStyle: 'bold', fontSize: 7 },
      margin: { left: M, right: M, top: TOP, bottom: pageH - BOTTOM },
      ...rest,
    });
    y = doc.lastAutoTable.finalY + 5;
  };
  const callout = (title, text, fill = AMBER_SURFACE, bar = AMBER) => {
    setText(8, 'normal');
    const lines = doc.splitTextToSize(pdfSafe(text), contentW - 10);
    const h = 10.5 + lines.length * 3.6;
    room(h + 4);
    doc.setFillColor(...fill);
    doc.rect(M, y, contentW, h, 'F');
    doc.setFillColor(...bar);
    doc.rect(M, y, 1.6, h, 'F');
    setText(9, 'bold', TEXT_BODY);
    doc.text(pdfSafe(title), M + 5, y + 5.5);
    setText(8, 'normal', TEXT_BODY);
    doc.text(lines, M + 5, y + 10);
    y += h + 4;
  };
  const tiles = (items) => {
    const gap = 3;
    const th = 24;
    const tw = (contentW - gap * (items.length - 1)) / items.length;
    room(th + 4);
    items.forEach((item, i) => {
      const tx = M + i * (tw + gap);
      doc.setFillColor(...SURFACE_RAISED);
      doc.roundedRect(tx, y, tw, th, 1.5, 1.5, 'F');
      setText(7, 'normal', TEXT_MUTED);
      doc.text(pdfSafe(item.label), tx + 3, y + 5);
      const value = pdfSafe(item.value);
      setText(value.length > 14 ? 10 : 13, 'bold', item.color || NAVY);
      doc.text(value, tx + 3, y + 12.5);
      if (item.sub) {
        setText(6.5, 'normal', TEXT_MUTED);
        // Up to two lines, so a sub-caption is wrapped rather than silently cut off.
        doc.text(doc.splitTextToSize(pdfSafe(item.sub), tw - 6).slice(0, 2), tx + 3, y + 17.5);
      }
    });
    y += th + 5;
  };

  // --- shared lookups ----------------------------------------------------------
  const intervalsByAsset = {};
  analysis.allIntervals.forEach((iv) => {
    if (!intervalsByAsset[iv.assetCode]) intervalsByAsset[iv.assetCode] = [];
    intervalsByAsset[iv.assetCode].push(iv);
  });
  const recs = {};
  rows.forEach((row) => {
    recs[row.assetCode] = recommendationFor(row, { intervals: intervalsByAsset[row.assetCode] || [], assumptions });
  });
  const colorOf = {};
  rows.forEach((row, i) => {
    colorOf[row.assetCode] = PALETTE[i % PALETTE.length];
  });
  const seriesOf = {};
  (analysis.utilisationSeries ? analysis.utilisationSeries.series : []).forEach((s) => {
    seriesOf[s.assetCode] = s;
  });
  const priced = rows.some((r) => isNum(r.paybackMonths) || isNum(r.replacementCostInr));
  const count = (category) => rows.filter((r) => recs[r.assetCode].category === category).length;

  // --- title bar -----------------------------------------------------------------
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 26, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(0, 26, pageW, 1.2, 'F');
  const logoW = 38;
  try {
    doc.addImage(ASSETPRO_LOGO_WHITE_PNG, 'PNG', pageW - M - logoW, 7, logoW, logoW / LOGO_ASPECT);
  } catch (e) {
    // A missing logo must never cost someone their report.
    setText(12, 'bold', [255, 255, 255]);
    doc.text('AssetPro', pageW - M, 13, { align: 'right' });
  }
  setText(15, 'bold', [255, 255, 255]);
  doc.text('DG Capacity Utilisation Report', M, 11);
  setText(8.5, 'normal', [255, 255, 255]);
  doc.text('Operating level and right-sizing screening for management review', M, 17);
  doc.text(
    pdfSafe(`${periodLabel}   |   Generated ${moment(generatedAt).format('DD MMM YYYY HH:mm')}${serverLabel ? `   |   ${serverLabel}` : ''}`),
    M,
    21.5
  );
  y = 33;

  // --- scope ------------------------------------------------------------------------
  const filterRows = describeFilters(filters);
  subheading('Report scope');
  table(
    null,
    [
      ['Period analysed', `${periodLabel}  (full-tank-to-full-tank intervals closing in this period)`],
      ...(filterRows.length
        ? filterRows.map(([label, value]) => [`Filter - ${label}`, value])
        : [['Filters applied', 'None - every DG with KWH metering in the period']]),
      ['DGs in this report', `${rows.length} of ${scope.assets.length} DG(s) with KWH metering in the period`],
      [
        'Data basis',
        `${f(scope.totalEntries)} AssetPro fuel entries; ${f(analysis.allIntervals.length)} measured full-tank-to-full-tank intervals`,
      ],
    ],
    {
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 1.4, textColor: TEXT_BODY },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55, textColor: NAVY } },
    }
  );
  callout(LIMITATION_TITLE, LIMITATION_TEXT);

  // --- 1. table -------------------------------------------------------------------
  section('1. DG utilisation table');
  const head = [
    'DG', 'Location', 'Model', 'Rated kVA', 'Intervals', 'Hours run', 'kWh generated', 'Fuel L', 'Avg kW',
    'Utilisation', 'kWh/L', 'P95 kW', 'Recommended\nkVA', 'Saving L/yr', ...(priced ? ['Payback months'] : []), 'Decision',
  ];
  const utilCol = 9;
  const decisionCol = head.length - 1;
  const included = rows.filter((r) => isNum(r.ratedKva) && r.intervalCount > 0);
  table(
    head,
    rows.map((r) => {
      const rec = recs[r.assetCode];
      const sized = rec.category === RECOMMENDATION.DOWNSIZE || rec.category === RECOMMENDATION.RETAIN;
      return [
        r.ownerCode ? `${r.assetCode}\n${r.ownerCode}` : r.assetCode,
        r.location || '-',
        r.assetModel || '-',
        kva(r.ratedKva),
        f(r.intervalCount),
        f(r.hours, 1),
        f(r.energyKwh),
        f(r.fuelL),
        f(r.avgKw, 1),
        pc(r.utilisation),
        f(r.efficiencyKwhPerL, 2),
        f(r.p95Kw, 1),
        sized ? kva(r.recommendedKva) : '-',
        f(r.annualisedSavingL),
        ...(priced ? [f(r.paybackMonths, 1)] : []),
        rec.decision,
      ];
    }),
    {
      foot: [[
        { content: `Portfolio - ${portfolio.assetsIncluded} DG(s) with a confirmed rating`, colSpan: 3 },
        f(portfolio.installedKva),
        f(included.reduce((s, r) => s + r.intervalCount, 0)),
        f(portfolio.hours, 1),
        f(portfolio.energyKwh),
        f(portfolio.fuelL),
        f(portfolio.avgKw, 1),
        pc(portfolio.weightedUtilisation),
        f(portfolio.efficiencyKwhPerL, 2),
        '',
        f(portfolio.recommendedKva),
        f(portfolio.annualisedSavingL),
        ...(priced ? [f(portfolio.paybackMonths, 1)] : []),
        '',
      ]],
      columnStyles: {
        0: { cellWidth: 22, fontStyle: 'bold' },
        1: { cellWidth: 20 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 13, halign: 'right' },
        4: { cellWidth: 13, halign: 'right' },
        5: { cellWidth: 15, halign: 'right' },
        6: { cellWidth: 17, halign: 'right' },
        7: { cellWidth: 15, halign: 'right' },
        8: { cellWidth: 13, halign: 'right' },
        9: { cellWidth: 16, halign: 'right', fontStyle: 'bold' },
        10: { cellWidth: 12, halign: 'right' },
        11: { cellWidth: 13, halign: 'right' },
        12: { cellWidth: 21, halign: 'right', fontStyle: 'bold' },
        13: { cellWidth: 16, halign: 'right' },
        ...(priced ? { 14: { cellWidth: 15, halign: 'right' } } : {}),
        [decisionCol]: { cellWidth: 21, fontStyle: 'bold' },
      },
      didParseCell: (d) => {
        // Numbers right-aligned in the head and the portfolio total, as in the body.
        if (d.section === 'head' || d.section === 'foot') {
          d.cell.styles.halign = d.column.index >= 3 && d.column.index < decisionCol ? 'right' : 'left';
        }
        if (d.section !== 'body') return;
        const r = rows[d.row.index];
        if (d.column.index === utilCol && isNum(r.utilisation)) {
          d.cell.styles.textColor = r.utilisation < 0.3 ? RED : r.utilisation < 0.5 ? AMBER : GREEN;
        }
        if (d.column.index === decisionCol) d.cell.styles.textColor = DECISION_COLOR[recs[r.assetCode].category];
      },
    }
  );
  para(
    'Utilisation colour: red below 30%, amber 30-50%, green 50% and above. ' +
      '"-" = not available or not applicable, never zero; the reason is given in the recommendations. Averages and ' +
      'utilisation are over the hours actually run. Saving L/yr is a capacity-only estimate from the fleet fuel ' +
      'curve (see appendix). Rated kVA was confirmed by the user in this report run - see appendix for how.',
    TEXT_MUTED,
    7
  );

  // --- 2. graphs ------------------------------------------------------------------
  newPage();
  section('2. Utilisation over the period');
  para(
    'Each point is one full-tank-to-full-tank interval, plotted at its closing full tank; its value is the average ' +
      'utilisation over the whole interval, not a reading at that moment. Lines break where an interval was excluded. ' +
      'Grey dashed line: the installed rating (100%). Green dashed line: the recommended smaller set\'s rating, expressed ' +
      'against the installed set - points above it are intervals whose average load a smaller set would not have carried.',
    TEXT_MUTED,
    7.5
  );

  const xMin = scope.rangeStartMs;
  const xMax = scope.rangeEndMs;
  const plotted = rows.filter((r) => seriesOf[r.assetCode]);
  subheading('All DGs', 116);
  drawChart(doc, {
    x: M,
    y,
    w: contentW,
    h: 104,
    xMin,
    xMax,
    title: 'Utilisation by interval - all DGs',
    subtitle: `${plotted.length} of ${rows.length} DG(s) plotted; DGs without a confirmed rating or a measured interval are not plotted`,
    lines: plotted.map((r) => ({ label: r.assetCode, color: colorOf[r.assetCode], points: seriesOf[r.assetCode].points })),
    refLines: [{ value: 100, label: '100% of rated output', color: TEXT_MUTED }],
    emptyText: plotted.length ? null : 'No DG has a measured interval with a confirmed rating',
    legend: true,
  });
  y += 110;

  subheading('Each DG', 72);
  const gap = 4;
  const cw = (contentW - gap) / 2;
  const ch = 60;
  rows.forEach((row, i) => {
    const col = i % 2;
    if (col === 0) room(ch + gap);
    const rec = recs[row.assetCode];
    const refLines = [];
    if (isNum(row.ratedKva)) {
      refLines.push({ value: 100, label: `${kva(row.ratedKva)} kVA installed (100%)`, color: TEXT_MUTED });
    }
    if (rec.category === RECOMMENDATION.DOWNSIZE) {
      refLines.push({
        value: (row.recommendedKva / row.ratedKva) * 100,
        label: `${kva(row.recommendedKva)} kVA recommended`,
        color: GREEN,
      });
    }
    let emptyText = null;
    if (!seriesOf[row.assetCode]) {
      emptyText = isNum(row.ratedKva) ? 'No measured interval in the period' : 'Not plotted - rated kVA not confirmed';
    }
    drawChart(doc, {
      x: M + col * (cw + gap),
      y,
      w: cw,
      h: ch,
      xMin,
      xMax,
      title: `${row.assetCode}${row.location ? ` - ${row.location}` : ''}${isNum(row.ratedKva) ? ` - ${kva(row.ratedKva)} kVA` : ''}`,
      subtitle:
        rec.category === RECOMMENDATION.NOT_ASSESSED
          ? rec.decision
          : `Avg ${pc(row.utilisation)}  |  P95 ${f(row.p95Kw, 1)} kW  |  ${rec.decision}` +
            (rec.category === RECOMMENDATION.DOWNSIZE ? ` -> ${kva(row.recommendedKva)} kVA` : ''),
      lines: seriesOf[row.assetCode]
        ? [{ label: row.assetCode, color: colorOf[row.assetCode], points: seriesOf[row.assetCode].points }]
        : [],
      refLines,
      emptyText,
    });
    if (col === 1 || i === rows.length - 1) y += ch + gap;
  });

  // --- 3. recommendations ---------------------------------------------------------
  newPage();
  section('3. Recommendations');
  const downsize = count(RECOMMENDATION.DOWNSIZE);
  tiles([
    { label: 'DGs in this report', value: f(rows.length), sub: `${portfolio.assetsIncluded} with a confirmed rating and measured load` },
    {
      label: 'Candidates for a smaller DG',
      value: f(downsize),
      color: downsize ? GREEN : NAVY,
      sub: `${count(RECOMMENDATION.RETAIN)} retain, ${count(RECOMMENDATION.VALIDATE)} validate metering, ${count(RECOMMENDATION.NOT_ASSESSED)} not assessed`,
    },
    {
      label: 'Installed -> recommended kVA',
      value: `${f(portfolio.installedKva)} -> ${f(portfolio.recommendedKva)}`,
      sub: isNum(portfolio.capacityReduction) ? `${pc(portfolio.capacityReduction)} capacity reduction` : 'Not every DG could be sized',
    },
    {
      label: 'Estimated fuel saving',
      value: isNum(portfolio.annualisedSavingL) ? `${f(portfolio.annualisedSavingL)} L/yr` : '-',
      sub: isNum(portfolio.annualisedSavingL)
        ? `From ${rows.filter((r) => isNum(r.annualisedSavingL) && r.annualisedSavingL > 0).length} DG(s) proposed for a smaller set; capacity-only estimate`
        : 'No annualised saving could be stated',
    },
    priced || isNum(portfolio.replacementCostInr)
      ? {
          label: 'Replacement cost and payback',
          value: rs(portfolio.replacementCostInr),
          sub: isNum(portfolio.paybackMonths)
            ? `Simple payback ${f(portfolio.paybackMonths, 1)} months`
            : 'Payback needs a price for every recommended size',
        }
      : { label: 'Replacement cost and payback', value: 'Prices not entered', sub: 'Enter diesel and DG prices in the report run' },
  ]);
  callout(
    `${LIMITATION_TITLE} - read before acting on any recommendation`,
    `${LIMITATION_TEXT} A DG shown as a candidate for a smaller set should go to a load study, not straight to purchase.`
  );

  const ordered = rows
    .slice()
    .sort(
      (a, b) =>
        DECISION_RANK[recs[a.assetCode].category] - DECISION_RANK[recs[b.assetCode].category] ||
        (b.annualisedSavingL || 0) - (a.annualisedSavingL || 0) ||
        a.assetCode.localeCompare(b.assetCode)
    );
  const recHead = [
    'DG', 'Decision', 'Current\nkVA', 'Recommended\nkVA', 'Avg\nutilisation', 'P95 kW', 'P95 load on\nrecommended',
    'Saving L/yr', ...(priced ? ['Payback months'] : []), 'Recommendation',
  ];
  const recDecisionCol = 1;
  table(
    recHead,
    ordered.map((r) => {
      const rec = recs[r.assetCode];
      const sized = rec.category === RECOMMENDATION.DOWNSIZE || rec.category === RECOMMENDATION.RETAIN;
      return [
        r.location ? `${r.assetCode}\n${r.location}` : r.assetCode,
        rec.decision,
        kva(r.ratedKva),
        sized ? kva(r.recommendedKva) : '-',
        pc(r.utilisation),
        f(r.p95Kw, 1),
        sized ? pc(r.p95LoadingAfter, 0) : '-',
        f(r.annualisedSavingL),
        ...(priced ? [f(r.paybackMonths, 1)] : []),
        rec.text,
      ];
    }),
    {
      columnStyles: {
        0: { cellWidth: 22, fontStyle: 'bold' },
        1: { cellWidth: 20, fontStyle: 'bold' },
        2: { cellWidth: 14, halign: 'right' },
        3: { cellWidth: 21, halign: 'right', fontStyle: 'bold' },
        4: { cellWidth: 16, halign: 'right' },
        5: { cellWidth: 14, halign: 'right' },
        6: { cellWidth: 21, halign: 'right' },
        7: { cellWidth: 16, halign: 'right' },
        ...(priced ? { 8: { cellWidth: 15, halign: 'right' } } : {}),
        [recHead.length - 1]: { cellWidth: 'auto' },
      },
      didParseCell: (d) => {
        if (d.section === 'body' && d.column.index === recDecisionCol) {
          d.cell.styles.textColor = DECISION_COLOR[recs[ordered[d.row.index].assetCode].category];
        }
      },
    }
  );

  subheading('Before approving a smaller DG', 34);
  CHECKLIST.forEach((item, i) => para(`${i + 1}. ${item}`, TEXT_BODY, 8));

  // --- appendix ----------------------------------------------------------------------
  newPage();
  section('Appendix - method, assumptions and data quality');
  subheading('Method');
  [
    'An interval runs between two consecutive filled-to-full fuel entries. Fuel = the closing full tank plus every entry recorded in between. Meter runs use AssetPro\'s cumulative calculated reading, so a meter replacement does not create a false run.',
    'Operating level (average kW) = kWh generated / hours run over the interval. Utilisation = average kW / (rated kVA x power factor).',
    'P95 kW = the 95th percentile of the interval average loads (linear interpolation, as Excel PERCENTILE.INC). Required kVA = P95 kW / (power factor x target loading).',
    'Recommended kVA = the smallest available standard size at or above the requirement, never larger than the installed set.',
    'Fuel saving = fleet capacity coefficient x (installed kVA - recommended kVA) x hours run, annualised by 365 / observed days. It holds delivered load and hours unchanged and excludes maintenance, ageing and OEM-specific fuel curves.',
    'No value is estimated to fill a gap. Intervals with a faulty-meter flag, a missing meter or fuel quantity, zero hours or a negative run are excluded and listed below.',
  ].forEach((line) => para(`- ${line}`, TEXT_BODY, 7.5));

  subheading('Assumptions used in this run', 40);
  const prices = Object.keys(priceMap)
    .map((k) => [Number(k), priceMap[k]])
    .filter(([k, v]) => isNum(k) && isNum(v))
    .sort((a, b) => a[0] - b[0]);
  table(
    ['Assumption', 'Value used'],
    [
      ['Power factor', f(assumptions.powerFactor, 2)],
      ['Target loading at P95', pc(assumptions.targetLoading, 0)],
      ['Maximum plausible utilisation', pc(assumptions.maxPlausibleUtilisation, 0)],
      ['Plausible efficiency band', `${assumptions.minPlausibleEfficiency}-${assumptions.maxPlausibleEfficiency} kWh/L`],
      ['Fuel-curve residual trim', `${assumptions.residualTrimPercent}%`],
      ['Minimum observed days before annualising', `${assumptions.minAnnualisationDays} days`],
      ['Minimum intervals for a firm P95', f(assumptions.minIntervalsForP95)],
      ['Diesel price', isNum(dieselPrice) ? `Rs ${f(dieselPrice, 2)} per litre` : 'Not entered - no fuel value or payback stated'],
      [
        'DG purchase prices entered',
        prices.length ? prices.map(([k, v]) => `${kva(k)} kVA: ${rs(v)}`).join('; ') : 'None - no replacement cost or payback stated',
      ],
    ],
    { columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } } }
  );

  subheading('Fleet fuel curve', 20);
  const curve = analysis.curve;
  if (curve && curve.ok) {
    para(
      `litres/hour = ${curve.capacityCoefficient.toFixed(5)} x installed kVA + ${curve.loadCoefficient.toFixed(5)} x average kW, ` +
        `fitted through the origin on ${curve.nFitted} of ${curve.nInput} usable intervals (${curve.nTrimmed} largest residuals ` +
        `removed), R-squared ${isNum(curve.rSquared) ? curve.rSquared.toFixed(3) : '-'}. The first coefficient is the fuel a set ` +
        'burns for its size alone; removing it is the only saving claimed.' +
        (curve.capacityCoefficientUsable ? '' : ' The capacity coefficient is not positive, so no fuel saving is claimed for any DG.'),
      TEXT_BODY,
      7.5
    );
  } else {
    para(`No fleet fuel curve could be fitted: ${curve ? curve.reason : 'no data'}. No fuel saving is claimed.`, AMBER, 7.5);
  }

  subheading('Rated kVA by asset model', 30);
  const codesInReport = new Set(rows.map((r) => r.assetCode));
  const modelsInReport = (scope.models || [])
    .map((m) => ({ ...m, inReport: m.assetCodes.filter((c) => codesInReport.has(c)) }))
    .filter((m) => m.inReport.length);
  table(
    ['Asset model', 'Sub type', 'DGs', 'Rated kVA used', 'How the rating was confirmed'],
    modelsInReport.map((m) => {
      const confirmed = modelKva[m.modelKey];
      const has = confirmed !== undefined && confirmed !== null && confirmed !== '' && isNum(Number(confirmed));
      return [m.modelName || 'No asset model', m.subTypeName || '-', f(m.inReport.length), has ? kva(Number(confirmed)) : '-', ratingBasis(m, confirmed)];
    }),
    {
      columnStyles: {
        0: { cellWidth: 55 },
        1: { cellWidth: 40 },
        2: { cellWidth: 12, halign: 'right' },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 'auto' },
      },
    }
  );
  para(
    'AssetPro holds no capacity field, so each rating was suggested from the model or sub-type name and confirmed or corrected by the user who ran this report.',
    TEXT_MUTED,
    7
  );

  subheading('Data quality and exclusions', 30);
  const dropped = (analysis.dropReasons || []).reduce((s, d) => s + d.count, 0);
  para(
    `${f(analysis.allIntervals.length)} interval(s) were measured and ${f(dropped)} were excluded. Excluded intervals are left out ` +
      'of every figure and every chart; they are never counted as zero.',
    TEXT_BODY,
    7.5
  );
  if (analysis.dropReasons && analysis.dropReasons.length) {
    table(
      ['Reason an interval was excluded', 'Intervals', 'Examples'],
      analysis.dropReasons.map((d) => [d.reason, f(d.count), d.examples.join(', ')]),
      { columnStyles: { 0: { cellWidth: 'auto' }, 1: { cellWidth: 18, halign: 'right' }, 2: { cellWidth: 90 } } }
    );
  }
  const notAssessed = rows.filter((r) => recs[r.assetCode].category === RECOMMENDATION.NOT_ASSESSED);
  if (notAssessed.length) {
    para(
      `Not assessed (${notAssessed.length}): ${notAssessed.map((r) => `${r.assetCode} - ${recs[r.assetCode].text}`).join('; ')}`,
      AMBER,
      7.5
    );
  }
  const suspect = rows.filter((r) => recs[r.assetCode].category === RECOMMENDATION.VALIDATE);
  if (suspect.length) {
    para(
      `Metering to validate (${suspect.length}): ${suspect.map((r) => `${r.assetCode} (${f(r.efficiencyKwhPerL, 2)} kWh/L)`).join(', ')}`,
      RED,
      7.5
    );
  }

  // --- running header and footer ---------------------------------------------------
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    if (i > 1) {
      setText(7, 'bold', NAVY);
      doc.text('DG Capacity Utilisation Report', M, 9);
      setText(7, 'normal', TEXT_MUTED);
      doc.text(pdfSafe(periodLabel), pageW - M, 9, { align: 'right' });
      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.2);
      doc.line(M, 11, pageW - M, 11);
    }
    setText(6.8, 'normal', TEXT_MUTED);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(M, pageH - 9.5, pageW - M, pageH - 9.5);
    doc.text(
      'AssetPro - DG capacity utilisation screening. Based on full-tank-to-full-tank averages, not peak demand. "-" = missing or not applicable, never zero.',
      M,
      pageH - 6
    );
    doc.text(`Page ${i} of ${pages}`, pageW - M, pageH - 6, { align: 'right' });
  }

  return doc;
};

/** Build and save the PDF. Returns the file name used. */
export const downloadDgReportPdf = (args) => {
  const doc = buildDgReportPdf(args);
  const [start, end] = args.scope.queriedRange;
  const fileName = `DG-Utilisation-Report_${moment(start).format('YYYYMMDD')}-${moment(end).format('YYYYMMDD')}.pdf`;
  doc.save(fileName);
  return fileName;
};

export default downloadDgReportPdf;
